/**
 * OLA Cosmetics - Shared Utilities
 */

/**
 * Debounce a function call
 */
export function debounce(fn, delay = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle a function call
 */
export function throttle(fn, limit = 100) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => { inThrottle = false; }, limit);
    }
  };
}

/**
 * Check if device prefers reduced motion
 */
export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if device is mobile
 */
export function isMobile() {
  return window.innerWidth < 750;
}

/**
 * Check if device is likely low-performance
 */
export function isLowEndDevice() {
  const cores = navigator.hardwareConcurrency || 4;
  const memory = navigator.deviceMemory || 4;
  return cores <= 2 || memory <= 2;
}

/**
 * Lock body scroll (for modals/drawers)
 */
export function lockScroll() {
  document.body.style.overflow = 'hidden';
  document.body.style.paddingInlineEnd = getScrollbarWidth() + 'px';
  if (window.OLA_ScrollEngine) window.OLA_ScrollEngine.stop();
}

/**
 * Unlock body scroll
 */
export function unlockScroll() {
  document.body.style.overflow = '';
  document.body.style.paddingInlineEnd = '';
  if (window.OLA_ScrollEngine) window.OLA_ScrollEngine.start();
}

/**
 * Get scrollbar width
 */
function getScrollbarWidth() {
  return window.innerWidth - document.documentElement.clientWidth;
}

/**
 * Trap focus within an element (for modals/drawers)
 */
export function trapFocus(container) {
  const focusable = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  container.addEventListener('keydown', handler);
  first?.focus();

  return () => container.removeEventListener('keydown', handler);
}

/**
 * Wait for an element to appear in DOM
 */
export function waitForElement(selector, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => { observer.disconnect(); reject(new Error('Timeout')); }, timeout);
  });
}

/**
 * Fetch and render a Shopify section (for AJAX section rendering)
 */
export async function fetchSection(sectionId, url = window.location.pathname) {
  const separator = url.includes('?') ? '&' : '?';
  const response = await fetch(`${url}${separator}section_id=${sectionId}`);
  if (!response.ok) throw new Error('Section fetch failed');
  return response.text();
}
